import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import { useAdjustPurchaseBudgetMutation } from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { formatVND, formatVNDSigned } from "../../constants"

const cell = { padding: "8px 10px", borderBottom: "1px solid #eef0f4", fontSize: 13, textAlign: "left" }
const head = { ...cell, fontWeight: 600, color: "#5a6478", background: "#f8f9fb", whiteSpace: "nowrap" }
const num = { textAlign: "right", whiteSpace: "nowrap" }

const SimpleTable = ({ columns, rows, rowKey, highlight }) => (
  <MDBox sx={{ overflowX: "auto", border: "1px solid #e2e6ed", borderRadius: "8px" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} style={{ ...head, ...(c.numeric ? num : null) }}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={rowKey(r)} style={highlight?.(r) ? { background: "#fef3f2" } : undefined}>
            {columns.map((c) => (
              <td key={c.key} style={{ ...cell, ...(c.numeric ? num : null) }}>
                {c.render(r)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </MDBox>
)

// Shows GET /purchases/{id}/budget-impact (PO amount vs the budget it holds)
// and, when canAdjust, confirms POST /budget-adjust. Closes on success; the
// user re-clicks Issue. When it can't be adjusted, points to the fixes: cancel
// and recreate the order, or pick another quote / drop lines on the PO.
export default function BudgetImpactDialog({ open, onClose, purchaseId, impact, onPickOtherQuote }) {
  const navigate = useNavigate()
  const [note, setNote] = useState("")
  const [adjust, { isLoading: adjusting }] = useAdjustPurchaseBudgetMutation()

  useEffect(() => {
    if (open) setNote("")
  }, [open])

  if (!impact) return null
  const tracks = impact.tracks || []
  const orders = impact.orders || []
  const diff = Number(impact.differenceAmount) || 0

  const handleAdjust = async () => {
    try {
      const after = await adjust({ id: purchaseId, note: note.trim() }).unwrap()
      if (after?.adjustRequired) {
        showToastError(after.message || "Budget chưa được tính lại xong. Vui lòng xem lại.")
        return
      }
      showToastSuccess("Đã tính lại budget. Bạn có thể Issue PO.")
      onClose()
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không tính lại được budget. Vui lòng thử lại."))
    }
  }

  return (
    <Dialog open={open} onClose={() => !adjusting && onClose()} fullWidth maxWidth="md">
      <DialogTitle>Tính lại budget cho {impact.purchaseCode || "PO"}</DialogTitle>
      <DialogContent>
        <MDBox pt={1} display="flex" flexDirection="column" gap={2}>
          <MDBox display="flex" gap={3} flexWrap="wrap">
            <MDBox>
              <MDTypography variant="caption" color="text" display="block">
                Tiền PO
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {formatVND(impact.purchaseAmount)}
              </MDTypography>
            </MDBox>
            <MDBox>
              <MDTypography variant="caption" color="text" display="block">
                Budget PO đang giữ
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {formatVND(impact.coveredAmount)}
              </MDTypography>
            </MDBox>
            <MDBox>
              <MDTypography variant="caption" color="text" display="block">
                Chênh lệch
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold" color={diff > 0 ? "error" : "success"}>
                {formatVNDSigned(diff)}
              </MDTypography>
            </MDBox>
          </MDBox>

          {impact.message && (
            <MDBox p={1.5} borderRadius="lg" bgColor="grey-100" display="flex" gap={1}>
              <Icon color={impact.canAdjust ? "warning" : "error"} fontSize="small">
                {impact.canAdjust ? "info" : "error"}
              </Icon>
              <MDTypography variant="caption" color="text">
                {impact.message}
              </MDTypography>
            </MDBox>
          )}

          {tracks.length > 0 && (
            <MDBox>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={0.75}>
                Budget theo category × center
              </MDTypography>
              <SimpleTable
                rowKey={(t) => `${t.budgetCategoryId}-${t.budgetCenterId}`}
                highlight={(t) => t.enough === false}
                rows={tracks}
                columns={[
                  {
                    key: "track",
                    header: "Category / Center",
                    render: (t) => (
                      <>
                        <div style={{ fontWeight: 600 }}>{t.budgetCategoryName}</div>
                        <div style={{ color: "#8a93a6", fontSize: 12 }}>{t.budgetCenterName}</div>
                      </>
                    ),
                  },
                  { key: "diff", header: "Cần thêm", numeric: true, render: (t) => formatVNDSigned(t.difference) },
                  {
                    key: "available",
                    header: "Available trước → sau",
                    numeric: true,
                    render: (t) =>
                      t.availableBefore == null
                        ? "Chưa cấp budget năm nay"
                        : `${formatVND(t.availableBefore)} → ${formatVND(t.availableAfter)}`,
                  },
                  {
                    key: "enough",
                    header: "",
                    render: (t) =>
                      t.enough === false ? (
                        <span style={{ color: "#b42318", fontWeight: 600 }}>Không đủ</span>
                      ) : (
                        <span style={{ color: "#0e7a57" }}>Đủ</span>
                      ),
                  },
                ]}
              />
            </MDBox>
          )}

          {orders.length > 0 && (
            <MDBox>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={0.75}>
                Tổng tiền OR sau khi tính lại
              </MDTypography>
              <SimpleTable
                rowKey={(o) => o.orderRequestId}
                rows={orders}
                columns={[
                  { key: "code", header: "OR", render: (o) => o.orderCode || `#${o.orderRequestId}` },
                  {
                    key: "total",
                    header: "Trước → sau",
                    numeric: true,
                    render: (o) => `${formatVND(o.totalBefore)} → ${formatVND(o.totalAfter)}`,
                  },
                  { key: "diff", header: "Chênh lệch", numeric: true, render: (o) => formatVNDSigned(o.difference) },
                ]}
              />
            </MDBox>
          )}

          {impact.canAdjust ? (
            <MDInput
              fullWidth
              multiline
              rows={2}
              labelType="non-sticky"
              labelText="Ghi chú (tuỳ chọn)"
              placeholder="VD: Giá thị trường tăng"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={adjusting}
            />
          ) : (
            <MDTypography variant="caption" color="text">
              Chưa thể tính lại budget. Bạn có thể huỷ order và tạo lại, hoặc chỉnh PO (chọn báo giá khác, bớt line)
              rồi xem lại.
            </MDTypography>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={adjusting}>
          Để sau
        </MDButton>
        {!impact.canAdjust && (
          <>
            {orders.map((o) => (
              <MDButton
                key={o.orderRequestId}
                variant="outlined"
                color="info"
                size="small"
                onClick={() => navigate(`/procurement/orders/${o.orderRequestId}`)}
              >
                Mở {o.orderCode || "OR"}
              </MDButton>
            ))}
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={() => {
                onClose()
                onPickOtherQuote?.()
              }}
            >
              Chọn báo giá khác
            </MDButton>
          </>
        )}
        {impact.canAdjust && (
          <MDButton variant="gradient" color="info" onClick={handleAdjust} disabled={adjusting}>
            {adjusting ? "Đang tính lại…" : "Xác nhận tính lại budget"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
